import { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const UNSUBSCRIBE_WEBHOOK_URL = import.meta.env.VITE_UNSUBSCRIBE_WEBHOOK_URL as string | undefined;
const UNSUBSCRIBE_WEBHOOK_USERNAME = import.meta.env.VITE_UNSUBSCRIBE_WEBHOOK_USERNAME as string | undefined;
const UNSUBSCRIBE_WEBHOOK_PASSWORD = import.meta.env.VITE_UNSUBSCRIBE_WEBHOOK_PASSWORD as string | undefined;

type UnsubscribeStatus = "idle" | "submitting" | "success" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get("email") || "";

  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<UnsubscribeStatus>("idle");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const canCallWebhook = useMemo(() => {
    return Boolean(UNSUBSCRIBE_WEBHOOK_URL && UNSUBSCRIBE_WEBHOOK_USERNAME && UNSUBSCRIBE_WEBHOOK_PASSWORD);
  }, []);

  const handleUnsubscribe = async () => {
    setStatus("submitting");
    setErrorMessage(null);

    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrorMessage("Informe um email válido.");
      return;
    }

    if (!canCallWebhook) {
      setStatus("success");
      return;
    }

    try {
      const credentials = btoa(`${UNSUBSCRIBE_WEBHOOK_USERNAME}:${UNSUBSCRIBE_WEBHOOK_PASSWORD}`);

      const response = await fetch(UNSUBSCRIBE_WEBHOOK_URL!, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Basic ${credentials}`,
        },
        body: JSON.stringify({
          timestamp: new Date().toISOString(),
          source: "aroan_landing_page",
          type: "unsubscribe",
          email,
          page: "/unsubscribe",
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setStatus("success");
    } catch (e) {
      console.error("Unsubscribe error:", e);
      setStatus("error");
      setErrorMessage("Não foi possível processar seu descadastro agora. Tente novamente mais tarde.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <SEO
        title="Cancelar inscrição"
        description="Cancele o recebimento de e-mails da Aroan."
        canonical="https://aroan.com.br/unsubscribe"
        ogType="website"
        article={false}
        noindex
      />
      <div className="w-full max-w-lg rounded-xl bg-white border p-6 shadow-sm">
        <h1 className="text-2xl font-bold">Cancelar inscrição</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Use esta página para parar de receber emails da AROAN.
        </p>

        {status === "success" ? (
          <div className="mt-6 space-y-3">
            <p className="text-sm">
              Se o email <strong>{email}</strong> estiver na nossa lista, ele foi marcado para não receber mais mensagens.
            </p>
            {!canCallWebhook && (
              <p className="text-sm text-muted-foreground">
                Observação: se você ainda receber mensagens, responda o último email com o assunto "REMOVER" para que possamos concluir manualmente.
              </p>
            )}
            <a href="/" className="text-sm text-primary hover:underline">
              Voltar para o site
            </a>
          </div>
        ) : (
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium" htmlFor="unsubscribe-email">
                Seu email
              </label>
              <Input
                id="unsubscribe-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seuemail@empresa.com"
              />
            </div>

            {status === "error" && errorMessage && (
              <p className="text-sm text-destructive">{errorMessage}</p>
            )}

            <Button onClick={handleUnsubscribe} disabled={status === "submitting"} className="w-full">
              {status === "submitting" ? "Processando..." : "Cancelar inscrição"}
            </Button>

            <p className="text-xs text-muted-foreground">
              Se você não solicitou nenhum material, pode cancelar a inscrição sem prejuízo.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
